// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';
import { FocusOn, MoveFocusInside } from 'react-focus-on';

import { AFTER_APP } from 'Components/frameworks/portal/Portal.constants';
import { ICONS } from 'Components/utils/icon/icons';

import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import Portal from 'Components/frameworks/portal/Portal.jsx';

import { themes } from './Backdrop.themes';

export type BackdropProps = {
  ariaLabel?: string,
  ariaLabelledby?: string,
  bgColor?: string,
  className?: string,
  closeLabel?: string,
  children: Node,
  externalFocusTrapRefs?: Array<{ current: ?HTMLElement }>,
  hasCloseButton?: boolean,
  hasNoFocusTrap?: boolean,
  onClose?: (e: SyntheticEvent<*>) => void,
  opacity?: number,
  portalName?: string,
  theme?: string,
};

export function Backdrop(props: BackdropProps) {
  const {
    ariaLabel,
    ariaLabelledby,
    bgColor = '#000',
    children,
    className,
    closeLabel,
    externalFocusTrapRefs,
    hasCloseButton,
    hasNoFocusTrap,
    onClose,
    opacity = 0.8,
    portalName = AFTER_APP,
    theme = 'default',
  } = props;

  if (!Children.count(children)) {
    return null;
  }

  const { thCloseButton, thCloseButtonLabel, thCloseButtonStyle } = themes[theme];

  const closeBtnLabel = closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : closeLabel;

  return (
    <Portal name={portalName}>
      <BodyLocker isLocked>
        <div className={classnames(className, 'fadeIn fixed inset-0 escape-responsive-helper z-3')}>
          <div
            className="fixed h-screen inset-0 z-1"
            data-testid="Backdrop-overlay"
            style={{
              backgroundColor: bgColor,
              opacity,
            }}
          />

          <div
            aria-label={ariaLabelledby ? null : ariaLabel}
            aria-labelledby={ariaLabelledby}
            role="dialog"
          >
            <div role="document">
              {hasCloseButton ? (
                <FocusOn
                  allowPinchZoom
                  enabled={!hasNoFocusTrap}
                  onEscapeKey={onClose}
                  shards={externalFocusTrapRefs}
                >
                  <MoveFocusInside>
                    <button
                      className={classnames('absolute top-0 z-3', thCloseButton)}
                      data-testid="Backdrop-close"
                      onClick={onClose}
                      title={closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : null}
                      type="button"
                      style={thCloseButtonStyle}
                    >
                      {closeBtnLabel && <span className={thCloseButtonLabel}>{closeBtnLabel}</span>}
                      <Icon name={ICONS.CROSS} width="1em" />
                    </button>
                  </MoveFocusInside>
                  <div className="absolute flex items-center justify-center inset-0 z-2">
                    {children}
                  </div>
                </FocusOn>
              ) : (
                <div className="absolute flex items-center justify-center inset-0 p-5 z-2">
                  <FocusOn
                    allowPinchZoom
                    className="max-w-full"
                    enabled={!hasNoFocusTrap}
                    onClickOutside={onClose}
                    onEscapeKey={onClose}
                    shards={externalFocusTrapRefs}
                  >
                    {children}
                  </FocusOn>
                </div>
              )}
            </div>
          </div>
        </div>
      </BodyLocker>
    </Portal>
  );
}
